import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc,
  updateDoc,
  writeBatch,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  average
} from 'firebase/firestore';
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { db, auth } from "../firebaseConfig";
import { useAuth } from '../AuthContext';
import { useAppSelector } from '../redux/store';
import getCurrentScreen from "../Helper/GetScreen";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DailyCallData {
  attempted: number;
  connected: number;
  date: Date;
}

const ConversionRatio: React.FC = () => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [dailyCallData, setDailyCallData] = useState<DailyCallData[]>([]);

  const getWeekDays = () => {
    const now = new Date();
    const days: { start: Date; end: Date; date: Date }[] = [];

    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);

      const start = new Date(date);
      start.setHours(0, 0, 0, 0);

      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      days.push({ start, end, date });
    }

    return days;
  };

  const formatDay = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  useEffect(() => {
    const fetchCompanyId = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          setCompanyId(userDocSnap.data().companyId);
        } else {
          console.error("User document not found");
        }
      }
    };
    fetchCompanyId();
  }, []);

  useEffect(() => {
    const fetchCallLogs = async () => {
      if (!companyId) return;

      try {
        const weekDays = getWeekDays();
        const callLogsRef = collection(db, `companies/${companyId}/callLogs`);
        const dailyData = await Promise.all(
          weekDays.map(async ({ start, end, date }) => {
            let attemptedQuery = query(callLogsRef,
              where('type', '==', 'OUTGOING'),
              where('callTimestamp', '>=', Timestamp.fromDate(start)),
              where('callTimestamp', '<=', Timestamp.fromDate(end))
            );
            let connectedQuery = query(callLogsRef,
              where('type', '==', 'OUTGOING'),
              where('duration', '>', 0),
              where('callTimestamp', '>=', Timestamp.fromDate(start)),
              where('callTimestamp', '<=', Timestamp.fromDate(end))
            );

            if (isAdmin && currentScreen === 'Admin') {
              attemptedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end))
              );
              connectedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('duration', '>', 0),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end))
              );
            } else if (isManager && currentScreen === 'Manager') {
              const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', currentUser?.uid)
              );
              const usersSnapshot = await getDocs(usersQuery);
              const users = usersSnapshot.docs.map(doc => doc.id);
              attemptedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end)),
                where('userId', 'in', users)
              );
              connectedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('duration', '>', 0),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end)),
                where('userId', 'in', users)
              );
            } else if (isStaff && currentScreen === 'Staff') {
              attemptedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end)),
                where('userId', '==', currentUser?.uid)
              );
              connectedQuery = query(callLogsRef,
                where('type', '==', 'OUTGOING'),
                where('duration', '>', 0),
                where('callTimestamp', '>=', Timestamp.fromDate(start)),
                where('callTimestamp', '<=', Timestamp.fromDate(end)),
                where('userId', '==', currentUser?.uid)
              );
            }

            const [attemptedSnapshot, connectedSnapshot] = await Promise.all([
              getCountFromServer(attemptedQuery),
              getCountFromServer(connectedQuery)
            ]);

            return {
              attempted: attemptedSnapshot.data().count,
              connected: connectedSnapshot.data().count,
              date
            };
          })
        );

        setDailyCallData(dailyData);
      } catch (error) {
        console.error("Error fetching call logs:", error);
      }
    };

    fetchCallLogs();
  }, [companyId]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: "This Week's Daily Conversion Ratio",
      },
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
        beginAtZero: true,
      },
    },
  };

  const data = {
    labels: dailyCallData.map(day => formatDay(day.date)),
    datasets: [
      {
        label: "Connected Calls",
        data: dailyCallData.map(day => day.connected),
        backgroundColor: "#8BFF8F",
        borderRadius: 33,
        barThickness: 18,
      },
      {
        label: "Attempted Calls",
        data: dailyCallData.map(day => day.attempted),
        backgroundColor: "#CADDEC",
        borderRadius: 33,
        barThickness: 18,
      },
    ],
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div
          className="flex items-center justify-center w-16 h-16 rounded-full"
          style={{
            background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/person-swap.svg`}
            alt="Icon"
            className="w-10 h-10"
          />
        </div>
        <h2
          className="text-2xl text-black font-medium"
          style={{
            fontFamily: "Montserrat",
            lineHeight: "29.26px",
          }}
        >
          Conversion Ratio
        </h2>
      </div>
      <div className="h-[300px]">
        <Bar options={options} data={data} />
      </div>
    </div>
  );
};

export default ConversionRatio;
